'use client'

import { Post } from '@/payload-types'
import Link from 'next/link'
import { useState } from 'react'
import { ChevronDown, ExternalLink } from 'lucide-react'
import { cn } from '@/utilities/ui'
import { slugToLink } from '@/utilities/slugToLink'

import { MediaWithFallback } from '@/components/frontend/MediaWithFallback'
import { ByAuthor } from './ByAuthor'
import RichText from '../payload/RichText'
import { FlatTitle } from './FlatTitle'

interface Props {
    post: Post
}

export function ExpandablePost({ post }: Props) {
    const [expanded, setExpanded] = useState(false)

    function onContinueReadingClick() {
        setExpanded(true)
    }

    return (
        <div className={cn('py-[18px] border-b-[1px] relative', { 'max-h-[636px] sm:max-h-[364px]': !expanded, 'max-h-none': expanded })}>
            <div className={cn({ 'max-h-[600px] sm:max-h-[326px] overflow-hidden': !expanded, 'max-h-none': expanded })}>
                <article className='flex flex-col-reverse sm:flex-row min-h-[150px] relative'>
                    <figure className='flex max-w-[300px] mt-4 sm:mt-0 mr-[19px] w-full [&_img]:max-w-[300px]'>
                        <MediaWithFallback media={post.heroImage} options={{ width: 340, height: 255 }} />
                    </figure>
                    <div>
                        <h2 className='text-2xl font-bold'>
                            <FlatTitle heading={post.title}></FlatTitle>
                        </h2>
                        <p className='text-[17px] leading-[1.7] my-[12px] font-serif'>{post.meta?.description}</p>
                        <ByAuthor authors={post.populatedAuthors} postId={post.id} />
                    </div>
                </article>
                <div className='mt-[17px]'>
                    <RichText className='text-xl' data={post.content} enableGutter={false} />
                    <div className='text-right'>
                        <Link href={slugToLink(post.slug)} target='_blank' className='text-[18px] font-bold text-black inline-flex'>
                            <span className='pr-[3px]'>
                                <ExternalLink className='stroke-1' />
                            </span>{' '}
                            open article in new tab
                        </Link>
                    </div>
                </div>
            </div>

            {!expanded && (
                <div
                    className={cn(
                        'absolute flex items-end bottom-4 right-0 left-0 h-[150px] max-w-[800px] w-full text-center bg-gradient-to-b from-transparent/0 to-[#f9f9f9]/100',
                    )}
                >
                    <button
                        className='p-[8px] bg-accent text-[18px] text-white hover:text-accent hover:bg-white max-w-[210px] rounded-lg h-[40px] mx-auto font-bold border-[4px] border-accent inline-flex items-center'
                        onClick={() => {
                            onContinueReadingClick()
                        }}
                    >
                        <div className='inline-flex'>
                            <span>Continue Reading</span>
                            <ChevronDown className='stroke-1' />
                        </div>
                    </button>
                </div>
            )}
        </div>
    )
}
